import * as React from "react"
import { Accordion as AccordionPrimitive } from "radix-ui"
import { cn } from "cn"
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

function Accordion({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Root>) {
  return (
    <AccordionPrimitive.Root
      data-slot="accordion"
      className={cn("flex w-full flex-col", className)}
      {...props}
    />
  )
}

function AccordionItem({
  className,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Item>) {
  return (
    <AccordionPrimitive.Item
      data-slot="accordion-item"
      className={cn(className)}
      {...props}
    />
  )
}

type AccordionTriggerProps = React.ComponentProps<
  typeof AccordionPrimitive.Trigger
> & {
  icons?: boolean
}

function AccordionTrigger({
  className,
  children,
  icons = true,
  ...props
}: AccordionTriggerProps) {
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        data-slot="accordion-trigger"
        className={cn(
          "focus-visible:border-ring group/accordion-trigger relative flex flex-1 items-center justify-start rounded-lg border border-transparent py-2.5 text-left text-sm font-medium transition-all outline-none disabled:pointer-events-none disabled:opacity-50",
          className
        )}
        {...props}
      >
        {icons && (
          <>
            <FontAwesomeIcon
              icon={faChevronDown}
              data-slot="accordion-trigger-icon"
              className="pointer-events-none size-4 shrink-0 pr-2 text-neutrals-700 group-aria-expanded/accordion-trigger:hidden"
            />
            <FontAwesomeIcon
              icon={faChevronUp}
              data-slot="accordion-trigger-icon"
              className="pointer-events-none hidden size-4 shrink-0 pr-2 text-neutrals-700 group-aria-expanded/accordion-trigger:inline"
            />
          </>
        )}
        {children}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

function AccordionContent({
  className,
  children,
  ...props
}: React.ComponentProps<typeof AccordionPrimitive.Content>) {
  return (
    <AccordionPrimitive.Content
      data-slot="accordion-content"
      className="overflow-hidden text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
      {...props}
    >
      <div
        className={cn(
          "h-(--radix-accordion-content-height) pt-0 pb-2.5 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-neutrals-700 [&_p:not(:last-child)]:mb-4",
          className
        )}
      >
        {children}
      </div>
    </AccordionPrimitive.Content>
  )
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent }
